import classNames from 'classnames';
import { ReactNode } from 'react';
import {
  getBorderSizeClass,
  getCornerStyleClassname,
  getReferralColsClassnames,
  Layout,
  Size,
} from '../../../constants';
import { getHeaderActionsMargin, HeaderActionProps } from './constants';
import { CardActions } from './CardActions';
import { Color } from '../../../context/theme/ThemeConstants';
import { useTheme } from '../../../context/theme/useTheme';
import { ThemeBackground, ThemePrimary, ThemeText } from '../../../context/theme/ThemeConstantsUpdated';
import { ResponsiveContainerCircle } from '../ResponsiveCircle';

const { primaryBorderIntense, primaryBorderMuted } = ThemePrimary;
const { borderBackground, borderBackgroundInverted, mainBackground, shadow } = ThemeBackground;
const { mainText } = ThemeText;

export const Card = (props: Props) => {
  const {
    id,
    className,
    bodyClassName,
    containerClassName,
    children,
    header,
    headerActions,
    footer,
    media,
    anchorId,
    border,
    paddingX,
    mobileCorners,
    isInverted,
    onClick,
  } = props;
  const {
    includeShadow,
    backgroundClassOverride,
    borderClassOverride,
    removePadding,
    removeBackground,
    paddingTop,
    paddingBottom,
    cornerStyle,
    forceLightStyle,
    removeFooterBorder,
    isDebug,
  } = props;
  const cornerClass = getCornerStyleClassname(cornerStyle, mobileCorners);
  const { isDarkMode } = useTheme();

  const hasHeaderActions = headerActions && headerActions.length > 0;
  const hasDropdownAction = hasHeaderActions && headerActions.find((aProps) => 'options' in aProps);
  const headerActionMargin = hasHeaderActions && header != null ? getHeaderActionsMargin(headerActions.length) : null;
  const borderClass =
    borderClassOverride ||
    (forceLightStyle
      ? isDarkMode
        ? primaryBorderIntense
        : primaryBorderMuted
      : isInverted
        ? borderBackgroundInverted
        : borderBackground);

  const paddingXClass =
    !removePadding &&
    (paddingX === Size.none
      ? ''
      : paddingX === Size.xl
        ? 'px-6 @md:px-8'
        : paddingX === Size.sm
          ? 'px-4 @lg:px-5 @2xl:px-6'
          : 'px-5 @lg:px-6');
  const paddingTopClass =
    !removePadding &&
    (paddingTop === Size.none
      ? ''
      : paddingTop === Size.xl
        ? 'pt-6 @md:pt-8'
        : paddingTop === Size.sm
          ? 'pt-4 @lg:pt-5 @2xl:pt-6'
          : 'pt-5 @lg:pt-6');
  const paddingBottomClass =
    !removePadding &&
    footer == null &&
    (paddingBottom === Size.none
      ? ''
      : paddingBottom === Size.xl
        ? 'pb-6 @md:pb-8'
        : paddingBottom === Size.sm
          ? 'pb-4 @lg:pb-5 @2xl:pb-6'
          : 'pb-5 @lg:pb-6');

  return (
    <div className={classNames('relative w-full @container', containerClassName)}>
      {anchorId && <div id={anchorId} />}
      {isDebug && <ResponsiveContainerCircle className="absolute right-1/2 top-0 z-[100] -translate-x-1/2" />}
      <div
        id={id}
        className={classNames(
          className,
          !removeBackground && (backgroundClassOverride ? backgroundClassOverride : mainBackground),
          removeBackground && 'bg-none',
          cornerClass,
          includeShadow && shadow,
          border && [borderClass, getBorderSizeClass(border)],
          onClick && 'cursor-pointer'
        )}
        onClick={onClick}
      >
        <div
          className={classNames(
            bodyClassName,
            !removePadding && [paddingXClass, paddingTopClass],
            paddingBottomClass,
            props.forceOverflowHidden && 'overflow-hidden',
            cornerClass
          )}
        >
          <div className={classNames(media != null ? getReferralColsClassnames() : 'flex items-start justify-between')}>
            {media && (
              <div
                className={classNames(
                  'mx-auto mb-0 max-w-full flex-shrink-0 @md:ml-0',
                  paddingX === Size.xl ? '@md:mr-8' : paddingX === Size.sm ? '@md:mr-4 @lg:mr-5' : '@md:mr-5 @lg:mr-6'
                )}
              >
                {media}
              </div>
            )}
            <div className="relative max-w-full flex-1">
              {header != null && (
                <div className={classNames(typeof header === 'string' && [borderClass, 'border-b-2 pb-3'])}>
                  <div className={classNames(headerActionMargin, 'relative min-h-7 align-bottom')}>
                    <>
                      {typeof header === 'string' ? (
                        <h2
                          className={classNames(
                            mainText,
                            headerActionMargin,
                            hasHeaderActions && 'pr-5',
                            'relative top-1 h-fit w-full pl-1 text-xl font-semibold sm:text-2xl'
                          )}
                        >
                          {header}
                        </h2>
                      ) : (
                        header
                      )}
                      {headerActions && (
                        <div
                          className={classNames(
                            hasDropdownAction && 'z-50',
                            'absolute right-0 top-1/2 -translate-y-1/2 translate-x-full transform sm:mt-0 sm:flex sm:flex-shrink-0 sm:items-center'
                          )}
                        >
                          {headerActions.map((aProps) => (
                            <CardActions key={JSON.stringify(aProps)} {...aProps} />
                          ))}
                        </div>
                      )}
                    </>
                  </div>
                </div>
              )}

              {!header && hasHeaderActions && (
                <div className={classNames('absolute right-0 mt-5 sm:mt-0 sm:flex sm:flex-shrink-0 sm:items-center')}>
                  {headerActions!.map((aProps) => (
                    <CardActions key={JSON.stringify(aProps)} {...aProps} />
                  ))}
                </div>
              )}

              <div className={classNames(!header && hasHeaderActions && headerActionMargin)}>{children}</div>
            </div>
          </div>
        </div>

        {footer != null && (
          <div className={classNames(cornerClass, 'rounded-t-none', paddingXClass)}>
            <div className={classNames(!removeFooterBorder && [borderClass, 'border-t-2 sm:rounded-b-lg'])}>
              {footer}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type Props = {
  id?: string;

  className?: string;
  bodyClassName?: string;
  containerClassName?: string;
  backgroundClassOverride?: string | string[];
  borderClassOverride?: string | string[];

  onClick?: () => void;

  removeBackground?: boolean;
  removePadding?: boolean;
  paddingTop?: Size.none | Size.sm | Size.xl;
  paddingBottom?: Size.none | Size.sm | Size.xl;
  paddingX?: Size.none | Size.sm | Size.xl;
  removeFooterBorder?: boolean;

  children: ReactNode;
  header?: ReactNode;
  headerActions?: HeaderActionProps;
  footer?: ReactNode;
  media?: ReactNode;

  anchorId?: string;
  cornerStyle?: Size;
  mobileCorners?: boolean;

  border?: Size;
  includeShadow?: boolean;

  forceLightStyle?: boolean;
  layout?: Layout;

  colorOverride?: Color;
  isInverted?: boolean;

  forceOverflowHidden?: boolean;
  isDebug?: boolean;
};
